import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Nav from "../Components/Nav/Nav";
import Login from "./Login";
import AdminMenu from "../Components/Admin/AdminMenu";
import AdminFormHeader from "../Components/Admin/AdminFormHeader";
import { ToastContainer, toast } from "react-toastify";
import { Sparkles, RotateCw, FileText, Video, Download, Layers } from "lucide-react";
import {
  generateFormation,
  extractPptxSource,
  extractLoomSource,
} from "../actions/formations.actions";
import { getFormationCategories } from "../actions/formationCategory.actions";
import { getKnwoledge } from "../actions/knowledge.actions";

const AdminFormationGenerate = () => {
  const userData = useSelector((state) => state.userReducer);
  const categories = useSelector((state) => state.formationCategoryReducer);
  const knowledgeList = useSelector((state) => state.knowledgeReducer);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [topic, setTopic] = useState("");
  const [level, setLevel] = useState("débutant");
  const [modulesCount, setModulesCount] = useState(""); // "" = automatique (l'IA décide)
  const [categoryId, setCategoryId] = useState("");
  const [sourceType, setSourceType] = useState("gpt");
  // Chaque type de source garde son propre contenu : changer de source dans le
  // menu déroulant ne fait rien perdre (brief collé, article choisi, pptx extrait),
  // et une extraction pptx qui se termine ne peut pas polluer un autre type.
  const [briefText, setBriefText] = useState("");
  const [knowledgeId, setKnowledgeId] = useState("");
  const [articleContent, setArticleContent] = useState("");
  const [pptxContent, setPptxContent] = useState("");
  // Fichier PowerPoint chargé : { fileName, slideCount, wordCount } (null = aucun)
  const [pptxMeta, setPptxMeta] = useState(null);
  const [pptxLoading, setPptxLoading] = useState(false);
  // Vidéos Loom : URL saisies, transcription récupérée et vidéos correspondantes.
  // loomFetchedUrls mémorise les URL de la dernière récupération : si l'admin les
  // modifie ensuite, la transcription est périmée et doit être récupérée à nouveau.
  const [loomUrls, setLoomUrls] = useState("");
  const [loomContent, setLoomContent] = useState("");
  // Aperçu : { modules: [{ title, videos }] | null, videos } (modules = structure imposée)
  const [loomPreview, setLoomPreview] = useState(null);
  const [loomFetchedUrls, setLoomFetchedUrls] = useState("");
  const [loomLoading, setLoomLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const isAllowed = userData.isAdmin || userData.isReferent;

  useEffect(() => {
    dispatch(getFormationCategories());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getKnwoledge());
  }, [dispatch]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    const sourceContent =
      { brief: briefText, knowledge: articleContent, pptx: pptxContent, loom: loomContent }[
        sourceType
      ] || "";
    if (!topic.trim()) {
      toast.error("Indiquez le sujet de la formation");
      return;
    }
    // Une source "brief"/"knowledge" sans contenu ferait générer l'IA sur ses
    // connaissances générales, sans erreur ni avertissement : on bloque.
    if (sourceType === "brief" && !sourceContent.trim()) {
      toast.error("Collez un brief : le contenu source est vide");
      return;
    }
    if (sourceType === "knowledge" && !sourceContent.trim()) {
      toast.error("Choisissez un article source");
      return;
    }
    if (sourceType === "pptx" && !sourceContent.trim()) {
      toast.error("Importez un fichier PowerPoint (.pptx)");
      return;
    }
    if (sourceType === "loom" && (!sourceContent.trim() || loomFetchedUrls !== loomUrls)) {
      toast.error("Récupérez la transcription des vidéos Loom avant de générer");
      return;
    }
    setGenerating(true);
    try {
      const { formation, generating: inBackground, partial, message, warning } = await dispatch(
        generateFormation({
          topic: topic.trim(),
          level,
          modulesCount: modulesCount ? Number(modulesCount) : null,
          categoryId: categoryId || null,
          sourceType,
          sourceContent: sourceType === "gpt" ? null : sourceContent,
          sourceUrl: sourceType === "loom" ? loomFetchedUrls : null,
        })
      );
      // Un toast émis ici serait détruit par la navigation (ToastContainer
      // local à chaque page) : on transmet le message via l'état de
      // navigation, affiché au montage de la page cible.
      const state = partial
        ? { warning: message || "Génération partielle" }
        : warning
        ? { warning }
        : inBackground
        ? { success: "Plan créé — l'IA rédige les leçons et les quizz en arrière-plan" }
        : { success: "Formation générée" };
      if (formation && formation.id) {
        navigate(`/admin/formations/${formation.id}/edition`, { state });
      } else {
        navigate("/admin/formations", { state });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Échec de la génération");
      setGenerating(false);
    }
  };

  // Envoie le .pptx au backend, qui en extrait le texte (rien n'est stocké) :
  // ce texte devient le contenu source de la génération, comme un brief collé.
  const handlePptxChange = async (e) => {
    const input = e.target;
    const file = input.files && input.files[0];
    if (!file) return;
    setPptxLoading(true);
    setPptxMeta(null);
    setPptxContent("");
    try {
      const { content, meta } = await dispatch(extractPptxSource(file));
      setPptxContent(content);
      setPptxMeta({ fileName: file.name, ...meta });
      // Pré-remplit le sujet depuis le nom du fichier si l'admin n'a rien saisi.
      if (!topic.trim()) {
        setTopic(file.name.replace(/\.pptx$/i, "").replace(/[_-]+/g, " ").trim());
      }
      if (meta && meta.warning) toast.warning(meta.warning);
    } catch (error) {
      toast.error(error.response?.data?.message || "Échec de la lecture du PowerPoint");
    } finally {
      setPptxLoading(false);
      // Toujours réinitialiser : re-sélectionner le MÊME fichier (corrigé dans
      // PowerPoint entre-temps) ne déclenche pas de onChange si value est inchangé.
      // L'affichage du fichier chargé s'appuie sur pptxMeta, pas sur l'input.
      input.value = "";
    }
  };

  // Récupère côté serveur la transcription Loom des vidéos saisies (rien n'est
  // stocké) : elle devient le contenu source de la génération, comme un brief collé.
  const handleLoomFetch = async () => {
    if (!loomUrls.trim()) {
      toast.error("Collez au moins une URL Loom");
      return;
    }
    const urls = loomUrls;
    setLoomLoading(true);
    try {
      const { content, meta } = await dispatch(extractLoomSource(urls));
      setLoomContent(content);
      setLoomPreview({ modules: meta.modules || null, videos: meta.videos || [] });
      setLoomFetchedUrls(urls);
      // Pré-remplit le sujet (en-tête de la liste ou titre de la 1re vidéo) si rien n'est saisi.
      if (!topic.trim() && meta.suggestedTopic) setTopic(meta.suggestedTopic);
      if (meta.warning) toast.warning(meta.warning);
    } catch (error) {
      setLoomContent("");
      setLoomPreview(null);
      setLoomFetchedUrls("");
      toast.error(error.response?.data?.message || "Échec de la récupération des vidéos Loom");
    } finally {
      setLoomLoading(false);
    }
  };

  const formatDuration = (s) =>
    s == null ? "" : `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")} s`;

  if (!userData || !userData.id || !isAllowed) return <Login />;

  return (
    <>
      <ToastContainer />
      <Nav />
      <div className="mainContainer">
        <AdminMenu />
        <div className="centerBlock_admin">
          <AdminFormHeader
            title="Générer une formation"
            subtitle="Décrivez votre sujet, l'IA rédige le plan, les leçons et les quizz."
          />

          <form className="admin-form" onSubmit={handleGenerate}>
            <div className="admin-form-field">
              <label htmlFor="topic">Sujet de la formation</label>
              <input
                type="text"
                id="topic"
                value={topic}
                placeholder="Ex : SEO local pour débutants"
                onChange={(e) => setTopic(e.target.value)}
                disabled={generating}
              />
            </div>

            <div className="admin-form-field">
              <label htmlFor="level">Niveau</label>
              <select
                id="level"
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                disabled={generating}
              >
                <option value="débutant">Débutant</option>
                <option value="intermédiaire">Intermédiaire</option>
                <option value="avancé">Avancé</option>
              </select>
            </div>

            <div className="admin-form-field">
              <label htmlFor="modulesCount">Nombre de modules</label>
              <select
                id="modulesCount"
                value={modulesCount}
                onChange={(e) => setModulesCount(e.target.value)}
                disabled={generating}
              >
                <option value="">Automatique (l'IA décide)</option>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <option key={n} value={n}>
                    {n} module{n > 1 ? "s" : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="admin-form-field">
              <label htmlFor="category">Catégorie</label>
              <select
                id="category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                disabled={generating}
              >
                <option value="">— Aucune —</option>
                {Array.isArray(categories) &&
                  categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="admin-form-field">
              <label htmlFor="sourceType">Source du contenu</label>
              <select
                id="sourceType"
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value)}
                disabled={generating}
              >
                <option value="gpt">Connaissances générales de l'IA</option>
                <option value="brief">À partir d'un brief / texte collé</option>
                <option value="knowledge">À partir d'un article Tutoriel</option>
                <option value="pptx">À partir d'un PowerPoint (.pptx)</option>
                <option value="loom">À partir d'une vidéo Loom</option>
              </select>
            </div>

            {sourceType === "brief" && (
              <div className="admin-form-field">
                <label htmlFor="sourceContent">Brief / contenu source</label>
                <textarea
                  id="sourceContent"
                  rows="6"
                  value={briefText}
                  placeholder="Collez ici le texte sur lequel l'IA doit se baser…"
                  onChange={(e) => setBriefText(e.target.value)}
                  disabled={generating}
                />
              </div>
            )}
            {sourceType === "knowledge" && (
              <div className="admin-form-field">
                <label htmlFor="knowledgeArticle">Article source</label>
                <select
                  id="knowledgeArticle"
                  disabled={generating}
                  // Contrôlé : la sélection survit à un aller-retour vers un autre
                  // type de source, en cohérence avec articleContent.
                  value={knowledgeId}
                  onChange={(e) => {
                    const list = Array.isArray(knowledgeList)
                      ? knowledgeList
                      : Object.values(knowledgeList || {});
                    const art = list.find((k) => String(k.id) === e.target.value);
                    setKnowledgeId(e.target.value);
                    setArticleContent(art ? art.texte || "" : "");
                    // Pré-remplit le titre depuis l'article choisi (l'admin peut l'ajuster).
                    if (art && art.title) setTopic(art.title);
                  }}
                >
                  <option value="" disabled>
                    — Choisir un article —
                  </option>
                  {(Array.isArray(knowledgeList)
                    ? knowledgeList
                    : Object.values(knowledgeList || {})
                  ).map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.title}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {sourceType === "pptx" && (
              <div className="admin-form-field">
                <label htmlFor="pptxFile">Fichier PowerPoint (.pptx)</label>
                <input
                  type="file"
                  id="pptxFile"
                  accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                  onChange={handlePptxChange}
                  disabled={generating || pptxLoading}
                />
                {pptxLoading && (
                  <p className="textePoucents">
                    <RotateCw size={16} /> Lecture du fichier en cours…
                  </p>
                )}
                {!pptxLoading && pptxMeta && (
                  <p className="textePoucents">
                    <FileText size={16} /> {pptxMeta.fileName} — {pptxMeta.slideCount}{" "}
                    diapositive{pptxMeta.slideCount > 1 ? "s" : ""}, {pptxMeta.wordCount} mots
                    extraits
                  </p>
                )}
              </div>
            )}

            {sourceType === "loom" && (
              <div className="admin-form-field">
                <label htmlFor="loomUrls">Vidéo(s) Loom</label>
                <textarea
                  id="loomUrls"
                  className="admin-form-textarea-short"
                  rows="6"
                  value={loomUrls}
                  placeholder={
                    "Paiement\nMettre un acompte    https://www.loom.com/share/…\nService\nCréer un service    https://www.loom.com/share/…"
                  }
                  onChange={(e) => setLoomUrls(e.target.value)}
                  disabled={generating || loomLoading}
                />
                <p className="admin-form-hint">
                  Une vidéo par ligne, précédée de son titre (facultatif). Une ligne sans lien
                  devient un module regroupant les vidéos qui suivent : chaque vidéo devient alors
                  une leçon. Vous pouvez coller directement les colonnes d'un tableur.
                </p>
                <button
                  type="button"
                  className="admin-form-secondary"
                  onClick={handleLoomFetch}
                  disabled={generating || loomLoading || !loomUrls.trim()}
                >
                  {loomLoading ? (
                    <>
                      <RotateCw size={16} /> Récupération de la transcription…
                    </>
                  ) : (
                    <>
                      <Download size={16} /> Récupérer la transcription
                    </>
                  )}
                </button>
                {!loomLoading && loomPreview && loomFetchedUrls === loomUrls && (
                  <div className="admin-form-sourcepreview">
                    <p className="admin-form-hint">
                      {loomPreview.modules
                        ? `Structure imposée : ${loomPreview.modules.length} module${
                            loomPreview.modules.length > 1 ? "s" : ""
                          }, une leçon par vidéo`
                        : "Sans modules : l'IA organise elle-même le plan à partir des transcriptions"}
                    </p>
                    {(loomPreview.modules || [{ title: null, videos: loomPreview.videos }]).map(
                      (mod, mi) => (
                        <div key={mi}>
                          {mod.title && (
                            <p className="admin-form-sourcemodule">
                              <Layers size={16} /> {mod.title}
                            </p>
                          )}
                          <ul className="admin-form-sourcelist">
                            {mod.videos.map((v) => (
                              <li key={v.id} className={v.ok ? "" : "is-failed"}>
                                <Video size={16} /> {v.title || v.id}
                                {v.ok
                                  ? ` — ${formatDuration(v.duration)}, ${v.wordCount} mots transcrits`
                                  : " — transcription indisponible"}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )
                    )}
                  </div>
                )}
                {!loomLoading && loomPreview && loomFetchedUrls !== loomUrls && (
                  <p className="textePoucents">
                    Les URL ont changé : récupérez à nouveau la transcription.
                  </p>
                )}
              </div>
            )}

            <div className="admin-form-actions">
              <button type="submit" disabled={generating || pptxLoading || loomLoading}>
                {generating ? (
                  <>
                    <RotateCw size={17} /> Génération en cours…
                  </>
                ) : (
                  <>
                    <Sparkles size={17} /> Générer la formation
                  </>
                )}
              </button>
            </div>
          </form>

          {generating && (
            <p className="textePoucents">
              <RotateCw size={16} /> L'IA prépare le plan de la formation. Les leçons et les quizz
              seront ensuite rédigés en arrière-plan…
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminFormationGenerate;
